import React, { useEffect, useState } from "react";
import { Col, Popover, PopoverBody, PopoverHeader, Table } from "reactstrap";
import { getProductSalesByPriceApi } from "../../../services/manage-product/productService";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isNaN(number)
    ? value
    : `£${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isNaN(number) ? value : number.toLocaleString();
};

const formatPercentage = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isNaN(number)
    ? `${value}%`
    : `${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const normalizeSales = (response) => {
  const sales = response?.data?.sales_by_price
    || response?.sales_by_price
    || response?.data
    || response;
  return Array.isArray(sales) ? sales : [];
};

const SalesByPricePopover = ({ productId, salePrice }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const targetId = `sales-by-price-trigger-${productId}`;

  useEffect(() => {
    setIsOpen(false);
    setSales(null);
    setLoading(false);
    setError("");
  }, [productId]);

  const loadSales = () => {
    if (!productId || loading || sales) return;

    setLoading(true);
    setError("");
    getProductSalesByPriceApi(productId)
      .then(({ response }) => setSales(normalizeSales(response)))
      .catch((requestError) => {
        setError(
          requestError.response?.data?.message
          || requestError.message
          || "Failed to load sales by price."
        );
      })
      .finally(() => setLoading(false));
  };

  const openPopover = () => {
    setIsOpen(true);
    loadSales();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPopover();
    }
  };

  return (
    <Col sm={6}>
      <div
        id={targetId}
        className="d-flex align-items-center justify-content-between py-3 border-bottom h-100"
        role="button"
        tabIndex={0}
        onClick={openPopover}
        onKeyDown={handleKeyDown}
        title="View sales by price"
        style={{ cursor: "pointer" }}
      >
        <div className="text-muted small mr-3">
          Current Sale Price <i className="fa fa-info-circle ml-1" />
        </div>
        <div className="font-weight-bold text-right">{formatCurrency(salePrice)}</div>
      </div>

      <Popover
        placement="left"
        target={targetId}
        isOpen={isOpen}
        toggle={() => setIsOpen((open) => !open)}
        container="body"
        popperClassName="sales-by-price-popover"
        boundariesElement="viewport"
      >
        <PopoverHeader>
          <div className="d-flex align-items-center justify-content-between" style={{ width: "100%" }}>
            <span>Sales by Price</span>
            <button
              type="button"
              className="close ml-3"
              aria-label="Close sales by price"
              onClick={() => setIsOpen(false)}
              style={{ fontSize: "20px", lineHeight: 1 }}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </PopoverHeader>
        <PopoverBody style={{ maxHeight: "55vh", overflow: "auto" }}>
          {loading && (
            <div className="text-center text-muted py-3">
              <i className="fa fa-spinner fa-spin mr-2" />
              Loading sales by price...
            </div>
          )}
          {!loading && error && (
            <div className="alert alert-danger mb-0">
              {error}
              <button type="button" className="btn btn-link btn-sm p-0 ml-2" onClick={loadSales}>
                Retry
              </button>
            </div>
          )}
          {!loading && !error && sales && sales.length === 0 && (
            <div className="text-center text-muted py-3">No sales-by-price details available.</div>
          )}
          {!loading && !error && sales?.length > 0 && (
            <Table size="sm" className="mb-0 text-nowrap">
              <thead>
                <tr>
                  <th>Price</th>
                  <th className="text-right">Sold</th>
                  <th className="text-right">Returned</th>
                  <th className="text-right">Net Sold</th>
                  <th className="text-right">Return Rate</th>
                  <th className="text-right">Sell-through Rate</th>
                  <th>First Sold</th>
                  <th>Last Sold</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale, index) => (
                  <tr key={`${sale.price}-${sale.first_sold_at}-${index}`}>
                    <td className="font-weight-bold">{formatCurrency(sale.price)}</td>
                    <td className="text-right">{formatNumber(sale.sold_quantity)}</td>
                    <td className="text-right">{formatNumber(sale.returned_quantity)}</td>
                    <td className="text-right">{formatNumber(sale.net_sold_quantity)}</td>
                    <td className="text-right">{formatPercentage(sale.return_rate)}</td>
                    <td className="text-right">{formatPercentage(sale.sell_through_rate)}</td>
                    <td>{formatDate(sale.first_sold_at)}</td>
                    <td>{formatDate(sale.last_sold_at)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </PopoverBody>
      </Popover>
    </Col>
  );
};

export default SalesByPricePopover;
